import {
	Alert,
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { Fragment } from "react";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { useStraightenerParameters } from "../../straightener-common/hooks/useStraightenerParameters";
import {
	GROUP_LABELS,
	GROUP_ORDER,
	type StraightenerParameter,
	optionLabel,
} from "../../straightener-common/parameterCatalog";
import type {
	StraightenerPlcProduct,
	StraightenerTrendRow,
} from "../models/StraightenerTrend";
import { COLOR_TARGET } from "./StraightenerTrendChart";

const fmt = (n: number | null | undefined): string => {
	if (n == null) return "—";
	return Number.isInteger(n) ? String(n) : n.toFixed(3);
};

const PROFILE_TYPES = [
	{ code: 0, name: "Round", productLabel: "rounds" },
	{ code: 1, name: "Rebar", productLabel: "rebar" },
	{ code: 2, name: "Angle", productLabel: "angles" },
	{
		code: 3,
		name: "Unequal Angle",
		productLabel: "unequal angles",
		inferred: true,
	},
	{ code: 4, name: "Unknown", productLabel: "profile 4" },
	{ code: 5, name: "Channel", productLabel: "channels" },
	{ code: 6, name: "Flat", productLabel: "flats" },
] as const;

// Catalog names are preserved on a match. Otherwise format the PLC dimensions
// for display only, using the resolver's inferred family when present.
const plcProductLabel = (
	product: StraightenerPlcProduct | null,
): string | null => {
	if (!product?.isStable) return null;
	if (product.productSizeId != null && product.productName)
		return product.productName;
	const isFlat =
		product.profileType === 6 || product.inferredProfile === "Flat";
	const dimensions = isFlat
		? [product.thickness, product.width]
		: [product.width, product.height, product.thickness];
	if (
		product.profileType == null ||
		dimensions.some((n) => n == null || !Number.isFinite(n))
	)
		return null;
	const size = dimensions
		.map((n) => {
			const rounded = Number((n as number).toFixed(3));
			return Number.isInteger(rounded)
				? String(rounded)
				: rounded.toFixed(3).replace(/^0\./, ".");
		})
		.join("x");
	const family = isFlat
		? "flats"
		: (PROFILE_TYPES.find((p) => p.code === product.profileType)
				?.productLabel ?? `profile ${product.profileType}`);
	return `${size} ${family}`;
};

// The recipe and the line value of one parameter are printed with the same
// decimals, so "2" next to "1.969" reads "2.000": two numbers that are compared
// have to be written the same way to be compared at a glance.
const fmtPair = (
	n: number | null | undefined,
	r: StraightenerTrendRow | undefined,
): string => {
	if (n == null) return "—";
	const decimals = [r?.recipe, r?.current].some(
		(v) => v != null && !Number.isInteger(Number(v.toFixed(3))),
	);
	return decimals ? n.toFixed(3) : String(Math.round(n));
};

// "0.800 – 0.900", "≥ 0.800", "≤ 0.900" — a band can be open on either side.
// Both ends carry decimals as soon as one of them does: "0.700 – 1" reads like
// a mistake rather than a range.
const bandLabel = (r: StraightenerTrendRow): string | null => {
	const ends = [r.min, r.max].filter((n): n is number => n != null);
	if (!ends.length) return null;
	const decimals = ends.some((n) => !Number.isInteger(n));
	const f = (n: number) => (decimals ? n.toFixed(3) : String(n));
	if (r.min != null && r.max != null) return `${f(r.min)} – ${f(r.max)}`;
	return r.min != null ? `≥ ${f(r.min)}` : `≤ ${f(r.max as number)}`;
};

const outOfBand = (r: StraightenerTrendRow | undefined): boolean =>
	!!r &&
	r.current != null &&
	((r.min != null && r.current < r.min) ||
		(r.max != null && r.current > r.max));

// Compared at the printed precision, so a reading that displays as its recipe is
// a match whatever the float underneath says.
const round3 = (n: number) => Number(n.toFixed(3));

// How far the reading is from the recipe, as a share of the recipe value. Null
// when there is nothing to compare, Infinity when the recipe is 0 and the
// reading is not: no percentage describes that, and it is as far off as it gets.
const deviationPct = (r: StraightenerTrendRow | undefined): number | null => {
	if (!r || r.current == null || r.recipe == null) return null;
	const cur = round3(r.current);
	const rec = round3(r.recipe);
	if (cur === rec) return 0;
	if (rec === 0) return Number.POSITIVE_INFINITY;
	return (Math.abs(cur - rec) / Math.abs(rec)) * 100;
};

// Font colour only, never a background: the tones stay muted so a panel full of
// small deviations does not read as an alarm. A select has no arithmetic
// distance, so it is either the option the recipe asks for or it is not.
const TONES = [
	{ upTo: 0, color: "#3d7a3a", label: "Match" },
	{ upTo: 5, color: "#c9a227", label: "≤ 5%" },
	{ upTo: 30, color: "#e0873a", label: "≤ 30%" },
	{
		upTo: Number.POSITIVE_INFINITY,
		color: mesfColors.error.main,
		label: "> 30%",
	},
] as const;

const deviationColor = (
	r: StraightenerTrendRow | undefined,
	numeric: boolean,
): string | undefined => {
	const pct = deviationPct(r);
	if (pct == null) return undefined;
	if (!numeric) return pct === 0 ? TONES[0].color : TONES[3].color;
	return TONES.find((t) => pct <= t.upTo)?.color;
};

const deviationLabel = (r: StraightenerTrendRow | undefined): string => {
	if (!r || r.current == null || r.recipe == null) return "—";
	const d = round3(r.current - r.recipe);
	const pct = deviationPct(r);
	const signed = `${d > 0 ? "+" : d < 0 ? "-" : ""}${Math.abs(d).toFixed(3)}`;
	if (pct == null || pct === 0) return signed;
	return Number.isFinite(pct) ? `${signed} (${pct.toFixed(1)}%)` : signed;
};

// Rec. sits to the LEFT of Line: the recipe is what the operator reads
// against, the line reading is what gets coloured.
const PARAM_COL_WIDTH = 112;
const ROLL_VALUE_WIDTH = 46;

// All six groups sit in one row, in this order: the product first because it
// frames every other number, then the rolls, then the rest in catalog order.
const ROLL_GROUP = "StraighteningRolls";
const DIMENSIONS_GROUP = "Dimensions";
const CARD_GROUPS = GROUP_ORDER.filter(
	(g) => g !== DIMENSIONS_GROUP && g !== ROLL_GROUP,
);

// The card title already says "Product Dimensions"; repeating "Product" on each
// row only pushed the numbers out of the card.
const dimensionName = (name: string) => name.replace(/^Product\s+/i, "");

// Card titles on a slate bar, not in blue: blue is the recipe's colour on this
// panel, as it is the Target line's in the chart above it, and slate carries no
// meaning next to the deviation colours.
const zoneHeaderSx = {
	color: mesfColors.common.white,
	bgcolor: mesfColors.grey[500],
	borderTopLeftRadius: "inherit",
	borderTopRightRadius: "inherit",
	fontWeight: 700,
	fontSize: "0.76rem",
	letterSpacing: "0.03em",
	textTransform: "uppercase",
	px: 1.25,
	py: 0.6,
	whiteSpace: "nowrap" as const,
	overflow: "hidden",
	textOverflow: "ellipsis",
} as const;

const columnLabelSx = {
	flexShrink: 0,
	fontSize: "0.66rem",
	fontWeight: 700,
	letterSpacing: "0.04em",
	textAlign: "right" as const,
	color: "text.secondary",
	textTransform: "none" as const,
};

const numberSx = {
	flexShrink: 0,
	fontSize: "0.86rem",
	fontWeight: 600,
	textAlign: "right" as const,
	fontVariantNumeric: "tabular-nums",
};

// Tight horizontal padding: MUI's default would add 32px to every one of the
// ten value columns and blow the table past the width the screen can give it.
const cellSx = { fontSize: "0.84rem", py: 0.15, px: 0.5 } as const;

interface Props {
	rows: StraightenerTrendRow[];
	plcProduct: StraightenerPlcProduct | null;
	// The recipe the values are compared against (the one the picker shows).
	// Null when the product has no recipe: the Recipe column stays empty.
	recipeLabel: string | null;
}

export const StraightenerSetupGrid = ({
	rows,
	recipeLabel,
	plcProduct,
}: Props) => {
	const { byGroup, rollCount } = useStraightenerParameters();
	const hasRecipe = !!recipeLabel;
	const productLabel = plcProductLabel(plcProduct);
	const profileType = plcProduct?.profileType;
	const profileName =
		PROFILE_TYPES.find((p) => p.code === profileType)?.name ?? "Unknown";
	const profileTooltip = (
		<Box>
			{PROFILE_TYPES.map((p) => (
				<Box
					key={p.code}
					sx={{ fontWeight: p.code === profileType ? 700 : 400 }}
				>
					{p.code} = {p.name}
					{"inferred" in p ? " (inferred)" : ""}
				</Box>
			))}
			{profileType != null &&
			!PROFILE_TYPES.some((p) => p.code === profileType) ? (
				<Box sx={{ fontWeight: 700 }}>{profileType} = Unknown</Box>
			) : null}
		</Box>
	);
	// The recipe value is only printed where the line differs from it: a green
	// line value already says "as the recipe asks", and repeating the same number
	// beside it was the text the panel could do without.
	// Nor where the recipe has no value for it: a dash there drew the eye to
	// something that does not matter.
	const showRecipe = (r: StraightenerTrendRow | undefined) =>
		hasRecipe && r?.recipe != null && deviationPct(r) !== 0;
	const byKey = new Map(
		rows.map((r) => [`${r.parameterCode}:${r.rollNumber}`, r]),
	);

	// The deviation lives here and only here: on the panel it competed with the
	// two values for the same width and read as a third number to reconcile.
	const cellTooltip = (
		p: StraightenerParameter,
		r: StraightenerTrendRow | undefined,
		roll: number,
	) => {
		if (!r) return "";
		const numeric = p.valueType !== "select";
		// A select shows what the option means, the same as the cell does — the
		// card must not say "1" where the grid says "Proximity".
		const show = (v: number | null | undefined) =>
			numeric ? fmtPair(v, r) : (optionLabel(p, v) ?? fmt(v));
		const band = bandLabel(r);
		// Always the same five lines, with "—" where there is nothing to say, so
		// the card keeps its shape from one cell to the next.
		const lines: [string, string][] = [
			["Recipe", show(r.recipe)],
			["Line", show(r.current)],
			["Deviation", numeric ? deviationLabel(r) : "—"],
			["Range", band ?? "—"],
			[
				"Status",
				band == null ? "—" : outOfBand(r) ? "Out of range" : "In range",
			],
		];
		return (
			<Box sx={{ fontSize: "0.72rem", lineHeight: 1.7 }}>
				<Box sx={{ fontWeight: 700, mb: 0.4 }}>
					{p.displayName}
					{roll > 0 ? ` · Roll #${roll}` : ""}
				</Box>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "auto auto",
						columnGap: 1.5,
					}}
				>
					{lines.map(([label, value]) => (
						<Fragment key={label}>
							<Box sx={{ opacity: 0.75 }}>{label}</Box>
							<Box
								sx={{
									textAlign: "right",
									fontVariantNumeric: "tabular-nums",
									fontWeight: value === "Out of range" ? 700 : 600,
								}}
							>
								{value}
							</Box>
						</Fragment>
					))}
				</Box>
			</Box>
		);
	};

	const rowCellSx = {
		py: 0.15,
		display: "flex",
		alignItems: "center",
		borderBottom: `1px solid ${mesfColors.grey[100]}`,
	} as const;

	// Three grid cells per parameter: the card is a grid whose two value columns
	// are as wide as their widest number, so six cards fit in one row and the width
	// left over goes to the names instead of to fixed number slots.
	const renderRow = (p: StraightenerParameter) => {
		const r = byKey.get(`${p.code}:0`);
		const numeric = p.valueType !== "select";
		// A select stores the option's numeric value; show what it means.
		const show = (v: number | null | undefined) =>
			numeric ? fmtPair(v, r) : (optionLabel(p, v) ?? fmt(v));
		const name =
			p.group === DIMENSIONS_GROUP
				? dimensionName(p.displayName)
				: p.displayName;
		const tip = cellTooltip(p, r, 0);
		return (
			<Fragment key={p.code}>
				{/* A long name ("CCL Mag Magnetization") wraps onto a second line
				    instead of being cut: an ellipsis hid which parameter it was. */}
				<Typography
					sx={{
						...rowCellSx,
						fontSize: "0.8rem",
						lineHeight: 1.2,
						color: "text.secondary",
						minWidth: 0,
						overflowWrap: "anywhere",
					}}
				>
					{name}
				</Typography>
				<Tooltip title={tip} placement="top" enterDelay={250}>
					<Typography
						sx={{
							...rowCellSx,
							...numberSx,
							whiteSpace: "nowrap",
							justifyContent: "flex-end",
							color: COLOR_TARGET,
							fontWeight: 400,
						}}
					>
						{showRecipe(r) ? show(r?.recipe) : ""}
					</Typography>
				</Tooltip>
				<Tooltip title={tip} placement="top" enterDelay={250}>
					<Typography
						sx={{
							...rowCellSx,
							...numberSx,
							whiteSpace: "nowrap",
							justifyContent: "flex-end",
							color: deviationColor(r, numeric),
						}}
					>
						{show(r?.current)}
					</Typography>
				</Tooltip>
			</Fragment>
		);
	};

	const renderCard = (group: keyof typeof GROUP_LABELS, flex: string) => {
		const params = byGroup[group] ?? [];
		if (!params.length) return null;
		return (
			<Paper
				key={group}
				variant="outlined"
				sx={{
					flex,
					minWidth: 0,
					// On a screen too narrow for one row a card wraps alone; capped, it
					// keeps its width instead of stretching across the whole panel.
					maxWidth: 340,
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* The column headers sit under the title, not beside it: at this card
				    width a two-word group name and two headers on the same line make
				    the title wrap, and then every row in that card starts lower than
				    its neighbours'. */}
				<Typography sx={zoneHeaderSx} title={GROUP_LABELS[group]}>
					{GROUP_LABELS[group]}
				</Typography>
				<Box
					sx={{
						px: 1,
						pt: 0.4,
						pb: 0.25,
						display: "grid",
						gridTemplateColumns: "minmax(0, 1fr) auto auto",
						columnGap: 1,
						alignItems: "stretch",
					}}
				>
					<span />
					<Box component="span" sx={{ ...columnLabelSx, pb: 0.2 }}>
						Rec.
					</Box>
					<Box component="span" sx={{ ...columnLabelSx, pb: 0.2 }}>
						{group === DIMENSIONS_GROUP ? "HMI" : "Line"}
					</Box>
					{params.map(renderRow)}
				</Box>
				{group === DIMENSIONS_GROUP ? (
					<Box sx={{ px: 1, pt: 0.75, borderTop: 1, borderColor: "divider" }}>
						<Typography sx={{ ...columnLabelSx, textAlign: "left", mb: 0.5 }}>
							Tracking PLC reading
						</Typography>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "1fr auto",
								columnGap: 1,
							}}
						>
							{(
								[
									["Width", plcProduct?.width],
									["Height", plcProduct?.height],
									["Thickness", plcProduct?.thickness],
									["Profile Type", plcProduct?.profileType],
								] as const
							).map(([label, value]) => (
								<Fragment key={label}>
									<Typography
										sx={{ fontSize: "0.8rem", color: "text.secondary" }}
									>
										{label}
									</Typography>
									{label === "Profile Type" ? (
										<Tooltip
											title={profileTooltip}
											placement="top"
											describeChild
										>
											<Typography
												tabIndex={0}
												sx={{ ...numberSx, cursor: "help" }}
											>
												{profileType == null
													? "—"
													: `${fmt(profileType)} (${profileName})`}
											</Typography>
										</Tooltip>
									) : (
										<Typography sx={numberSx}>{fmt(value)}</Typography>
									)}
								</Fragment>
							))}
						</Box>
					</Box>
				) : null}
				{group === DIMENSIONS_GROUP && productLabel ? (
					<Alert
						severity={plcProduct?.productSizeId != null ? "success" : "warning"}
						aria-label={`${plcProduct?.productSizeId != null ? "Matched catalog product" : "No catalog match"}: ${productLabel}`}
						sx={{
							m: 0.75,
							py: 0,
							px: 0.75,
							fontSize: "0.8rem",
							fontWeight: 700,
							border: 1,
							borderColor:
								plcProduct?.productSizeId != null
									? "success.light"
									: "warning.light",
						}}
					>
						{productLabel}
					</Alert>
				) : null}
			</Paper>
		);
	};

	const rollParams = byGroup[ROLL_GROUP] ?? [];
	const rolls = Array.from({ length: rollCount }, (_, i) => i + 1);

	const rollHeadSx = {
		...cellSx,
		fontWeight: 600,
		color: "text.secondary",
		whiteSpace: "nowrap" as const,
		borderBottom: `1px solid ${mesfColors.grey[200]}`,
	} as const;
	const rollValueSx = {
		...cellSx,
		width: ROLL_VALUE_WIDTH,
		fontWeight: 600,
		fontVariantNumeric: "tabular-nums",
		whiteSpace: "nowrap" as const,
	} as const;

	const rollsCard = rollParams.length ? (
		<Paper variant="outlined" sx={{ flex: "0 0 auto" }}>
			<Typography sx={zoneHeaderSx}>{GROUP_LABELS[ROLL_GROUP]}</Typography>
			{/* width auto, not the table default of 100%: stretched, all the leftover
			    width lands on the Parameter column and pushes every number away from
			    the name it belongs to. */}
			<Table size="small" sx={{ width: "auto" }}>
				<TableHead>
					<TableRow>
						<TableCell
							rowSpan={2}
							sx={{
								...rollHeadSx,
								width: PARAM_COL_WIDTH,
								whiteSpace: "nowrap",
							}}
						>
							Parameter
						</TableCell>
						{rolls.map((roll) => (
							<TableCell
								key={roll}
								colSpan={2}
								align="center"
								sx={{
									...rollHeadSx,
									borderLeft: `1px solid ${mesfColors.grey[200]}`,
								}}
							>
								Roll #{roll}
							</TableCell>
						))}
					</TableRow>
					<TableRow>
						{/* The roll separator travels with the first column of the pair,
						    Recipe, so it falls between rolls and never inside one. */}
						{rolls.map((roll) => [
							<TableCell
								key={`r${roll}`}
								align="right"
								sx={{
									...cellSx,
									...columnLabelSx,
									width: ROLL_VALUE_WIDTH,
									borderBottom: `1px solid ${mesfColors.grey[200]}`,
									borderLeft: `1px solid ${mesfColors.grey[200]}`,
								}}
							>
								Rec.
							</TableCell>,
							<TableCell
								key={`c${roll}`}
								align="right"
								sx={{
									...cellSx,
									...columnLabelSx,
									width: ROLL_VALUE_WIDTH,
									borderBottom: `1px solid ${mesfColors.grey[200]}`,
								}}
							>
								Line
							</TableCell>,
						])}
					</TableRow>
				</TableHead>
				<TableBody>
					{rollParams.map((p) => (
						<TableRow key={p.code} hover>
							<TableCell
								sx={{
									...cellSx,
									color: "text.secondary",
									whiteSpace: "nowrap",
								}}
							>
								{p.displayName}
							</TableCell>
							{rolls.map((roll) => {
								const r = byKey.get(`${p.code}:${roll}`);
								const tip = cellTooltip(p, r, roll);
								return [
									<Tooltip
										key={`r${roll}`}
										title={tip}
										placement="top"
										enterDelay={250}
									>
										<TableCell
											align="right"
											sx={{
												...rollValueSx,
												color: COLOR_TARGET,
												fontWeight: 400,
												borderLeft: `1px solid ${mesfColors.grey[200]}`,
											}}
										>
											{showRecipe(r) ? fmtPair(r?.recipe, r) : ""}
										</TableCell>
									</Tooltip>,
									<Tooltip
										key={`c${roll}`}
										title={tip}
										placement="top"
										enterDelay={250}
									>
										<TableCell
											align="right"
											sx={{
												...rollValueSx,
												color: deviationColor(r, true),
											}}
										>
											{fmtPair(r?.current, r)}
										</TableCell>
									</Tooltip>,
								];
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Paper>
	) : null;

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					gap: 1,
					mb: 0.5,
					flexWrap: "wrap",
				}}
			>
				<Typography
					variant="subtitle2"
					sx={{ fontWeight: 700, color: "text.secondary" }}
				>
					Recipe vs live values
				</Typography>
				<Typography variant="caption" sx={{ color: "text.secondary" }}>
					{recipeLabel ? `Recipe: ${recipeLabel}` : "No recipe"}
				</Typography>
				{/* What the colours of the Current values mean, measured against the
				    recipe value. */}
				<Box
					sx={{
						ml: "auto",
						display: "flex",
						alignItems: "center",
						gap: 1.5,
					}}
				>
					{TONES.map((t) => (
						<Box
							key={t.label}
							sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
						>
							<Box
								component="span"
								sx={{
									width: 9,
									height: 9,
									borderRadius: "50%",
									bgcolor: t.color,
									flexShrink: 0,
								}}
							/>
							<Typography
								component="span"
								sx={{ fontSize: "0.7rem", color: "text.secondary" }}
							>
								{t.label}
							</Typography>
						</Box>
					))}
				</Box>
			</Box>

			{/* One row, left to right. The rolls table takes the width it needs and
			    the cards share what is left, all stretched to one height. Below the
			    width they need, cards wrap instead of scrolling sideways. */}
			<Box
				sx={{
					display: "flex",
					gap: 1,
					alignItems: "stretch",
					flexWrap: "wrap",
				}}
			>
				{/* Keep dimensions readable even when the product badge is hidden. */}
				{renderCard(DIMENSIONS_GROUP, "0 0 250px")}
				{rollsCard}
				{CARD_GROUPS.map((g) =>
					renderCard(
						g,
						g === "Entry" || g === "CutToLength" ? "1 1 150px" : "1 1 190px",
					),
				)}
			</Box>
		</Box>
	);
};
